import type { FC, ReactNode } from 'react'
import type { BagItem } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

import { BAG_STORAGE_KEY, MAX_ITEM_QUANTITY } from '@/constants/site'

type NewBagItem = Omit<BagItem, 'key' | 'quantity'>

interface BagContextValue {
    items: Array<BagItem>
    ready: boolean
    count: number
    notice: BagItem | null
    addItem: (item: NewBagItem, quantity?: number) => void
    updateQuantity: (key: string, quantity: number) => void
    removeItem: (key: string) => void
    clear: () => void
    dismissNotice: () => void
}

interface BagProviderProps {
    children: ReactNode
}

const BagContext = createContext<BagContextValue | null>(null)

const itemKey = (productId: string, size: string | null): string => `${productId}::${size ?? ''}`

const clampQuantity = (value: number): number => Math.min(MAX_ITEM_QUANTITY, Math.max(1, Math.round(value) || 1))

const sanitize = (value: unknown): Array<BagItem> => {
    if (!Array.isArray(value)) return []

    return value.flatMap((entry): Array<BagItem> => {
        if (!entry || typeof entry !== 'object') return []

        const item = entry as Record<string, unknown>

        if (typeof item.productId !== 'string' || typeof item.slug !== 'string' || typeof item.name !== 'string') return []

        const size = typeof item.size === 'string' && item.size ? item.size : null

        return [{
            key: itemKey(item.productId, size),
            productId: item.productId,
            slug: item.slug,
            name: item.name,
            color: typeof item.color === 'string' ? item.color : null,
            image: typeof item.image === 'string' ? item.image : null,
            imageAlt: typeof item.imageAlt === 'string' ? item.imageAlt : null,
            price: Number(item.price) || 0,
            size,
            quantity: clampQuantity(Number(item.quantity))
        }]
    })
}

const persist = (items: Array<BagItem>): void => {
    try {
        window.localStorage.setItem(BAG_STORAGE_KEY, JSON.stringify(items))
    }

    catch {
        // Storage can be unavailable (private mode, quota). The bag still works for this visit.
    }
}

export const BagProvider: FC<BagProviderProps> = ({ children }): ReactNode => {
    const [items, setItems] = useState<Array<BagItem>>([])
    const [ready, setReady] = useState(false)
    const [notice, setNotice] = useState<BagItem | null>(null)
    const itemsRef = useRef<Array<BagItem>>([])

    useEffect(() => {
        try {
            const saved = window.localStorage.getItem(BAG_STORAGE_KEY)

            if (saved) {
                const restored = sanitize(JSON.parse(saved))

                itemsRef.current = restored
                setItems(restored)
            }
        }

        catch {
            // A corrupted entry is ignored and replaced on the next change.
        }

        setReady(true)

        const onStorage = (event: StorageEvent): void => {
            if (event.key !== BAG_STORAGE_KEY) return

            try {
                const next = sanitize(event.newValue ? JSON.parse(event.newValue) : [])

                itemsRef.current = next
                setItems(next)
            }

            catch {
                // Ignore malformed values written elsewhere.
            }
        }

        window.addEventListener('storage', onStorage)

        return () => window.removeEventListener('storage', onStorage)
    }, [])

    const commit = useCallback((next: Array<BagItem>): void => {
        itemsRef.current = next
        setItems(next)
        persist(next)
    }, [])

    const addItem = useCallback((item: NewBagItem, quantity = 1): void => {
        const key = itemKey(item.productId, item.size)
        const current = itemsRef.current
        const existing = current.find(entry => entry.key === key)

        const next = existing
            ? current.map(entry => entry.key === key ? { ...entry, ...item, key, quantity: clampQuantity(entry.quantity + quantity) } : entry)
            : [...current, { ...item, key, quantity: clampQuantity(quantity) }]

        commit(next)
        setNotice(next.find(entry => entry.key === key) ?? null)
    }, [commit])

    const updateQuantity = useCallback((key: string, quantity: number): void => {
        commit(itemsRef.current.map(entry => entry.key === key ? { ...entry, quantity: clampQuantity(quantity) } : entry))
    }, [commit])

    const removeItem = useCallback((key: string): void => {
        commit(itemsRef.current.filter(entry => entry.key !== key))
    }, [commit])

    const clear = useCallback((): void => {
        commit([])
        setNotice(null)
    }, [commit])

    const dismissNotice = useCallback((): void => setNotice(null), [])

    const value = useMemo<BagContextValue>(() => ({
        items,
        ready,
        count: items.reduce((total, entry) => total + entry.quantity, 0),
        notice,
        addItem,
        updateQuantity,
        removeItem,
        clear,
        dismissNotice
    }), [items, ready, notice, addItem, updateQuantity, removeItem, clear, dismissNotice])

    return (
        <BagContext.Provider value={value}>
            {children}
        </BagContext.Provider>
    )
}

export const useBag = (): BagContextValue => {
    const context = useContext(BagContext)

    if (!context) throw new Error('useBag must be used inside BagProvider.')

    return context
}
